import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Collapsible panel used to stack several admin tools inside one console tab
 * (see the Communication tab), so each can be opened on its own rather than
 * scrolling past everything at once.
 */
const AdminCollapsibleSection = ({
  title,
  description,
  icon: Icon,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
  className = '',
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = typeof openProp === 'boolean';
  const open = isControlled ? openProp : uncontrolledOpen;
  const setOpen = (next) => {
    const value = typeof next === 'function' ? next(open) : next;
    if (!isControlled) setUncontrolledOpen(value);
    onOpenChange?.(value);
  };

  return (
    <div className={cn('rounded-xl border border-gray-700 bg-gray-800/40 overflow-hidden', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left transition-colors hover:bg-gray-700/40"
      >
        <span className="flex items-center gap-3 min-w-0">
          {Icon && (
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-500/15 border border-red-400/20">
              <Icon className="h-4 w-4 text-red-300" />
            </span>
          )}
          <span className="min-w-0">
            <span className="block truncate font-semibold text-white">{title}</span>
            {description && (
              <span className="block truncate text-xs text-gray-400">{description}</span>
            )}
          </span>
        </span>
        <ChevronDown
          className={cn('h-5 w-5 flex-shrink-0 text-gray-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="border-t border-gray-700 p-4">
          {children}
        </div>
      )}
    </div>
  );
};

export default AdminCollapsibleSection;
